import type {
  AlertView,
  ApiClientView,
  ApprovalRequestView,
  AuditLogView,
  BillingInvoice,
  Customer,
  DashboardCards,
  DashboardCharts,
  DeliveryView,
  DisputeView,
  FeatureFlagView,
  InvestigationListItem,
  LoginResponse,
  NotificationView,
  OutboxEventView,
  PagedResponse,
  PaymentDetail,
  PaymentLink,
  PaymentListItem,
  PaymentPeriodSummary,
  PaymentProofRow,
  PaymentProofSummary,
  Plan,
  Product,
  ProductAnalytics,
  ProviderReconciliationItemRow,
  ProviderReconciliationReport,
  ProviderView,
  ReconciliationDashboard,
  ReconciliationItemView,
  ReconciliationView,
  RefundListItem,
  ReportDefinition,
  ReportResult,
  RoleView,
  SettingView,
  SettlementView,
  Subscription,
  SystemHealth,
  TenantView,
  UserListItem,
  UserProfile,
  WebhookEventView,
} from '@barahpay/shared';
import { del, download, get, getPaged, post, put, upload } from './client';

/**
 * Every admin call in one place, mirroring the API surface. Keeping them together makes the portal's
 * dependency on the backend contract explicit and easy to review against the OpenAPI document
 * (spec section 144).
 */
export const api = {
  auth: {
    login: (email: string, password: string, mfaCode?: string) =>
      post<LoginResponse>('/api/v1/auth/login', { email, password, mfaCode }),
    refresh: (refreshToken: string) => post<LoginResponse>('/api/v1/auth/refresh', { refreshToken }),
    logout: (refreshToken: string) => post<unknown>('/api/v1/auth/logout', { refreshToken }),
    me: () => get<UserProfile>('/api/v1/auth/me'),
    changePassword: (currentPassword: string, newPassword: string) =>
      post<unknown>('/api/v1/auth/change-password', { currentPassword, newPassword }),
    beginMfa: () => post<{ secret: string; provisioningUri: string }>('/api/v1/auth/mfa/begin'),
    confirmMfa: (code: string) => post<unknown>('/api/v1/auth/mfa/confirm', { code }),
    disableMfa: (code: string) => post<unknown>('/api/v1/auth/mfa/disable', { code }),
  },

  dashboard: {
    cards: (productId?: string) => get<DashboardCards>('/api/v1/admin/dashboard/cards', { productId }),
    charts: (days = 30, productId?: string) =>
      get<DashboardCharts>('/api/v1/admin/dashboard/charts', { days, productId }),
    productAnalytics: (from: string, to: string) =>
      get<ProductAnalytics[]>('/api/v1/admin/dashboard/product-analytics', { from, to }),
    reconciliation: (settlementDate: string) =>
      get<ReconciliationDashboard>('/api/v1/admin/dashboard/reconciliation', { settlementDate }),
  },

  payments: {
    search: (params: Record<string, unknown>) => getPaged<PaymentListItem>('/api/v1/admin/payments', params),
    detail: (paymentNumber: string) => get<PaymentDetail>(`/api/v1/admin/payments/${encodeURIComponent(paymentNumber)}`),
    cancel: (paymentNumber: string, reason?: string) =>
      post<unknown>(`/api/v1/admin/payments/${encodeURIComponent(paymentNumber)}/cancel`, { reason }),
    syncProvider: (paymentNumber: string) =>
      post<string>(`/api/v1/admin/payments/${encodeURIComponent(paymentNumber)}/sync-provider`),
    void: (paymentNumber: string, reason?: string) =>
      post<unknown>(`/api/v1/admin/payments/${encodeURIComponent(paymentNumber)}/void`, { reason }),
    openInvestigation: (paymentNumber: string, reason: string, notes: string) =>
      post<InvestigationListItem>(`/api/v1/admin/payments/${encodeURIComponent(paymentNumber)}/investigations`, {
        reason,
        notes,
      }),
    resolveInvestigation: (paymentNumber: string, resolution: string, notes: string) =>
      post<InvestigationListItem>(
        `/api/v1/admin/payments/${encodeURIComponent(paymentNumber)}/investigations/resolve`,
        { resolution, notes },
      ),
    requestRefund: (paymentNumber: string, reason: string, amount?: number) =>
      post<unknown>(`/api/v1/payments/${encodeURIComponent(paymentNumber)}/refund`, { amount, reason }),
    recordManual: (body: Record<string, unknown>) => post<unknown>('/api/v1/manual-payments', body),
    approveManual: (paymentNumber: string, notes?: string) =>
      post<unknown>(`/api/v1/manual-payments/${encodeURIComponent(paymentNumber)}/approve`, { notes }),
    rejectManual: (paymentNumber: string, reason: string) =>
      post<unknown>(`/api/v1/manual-payments/${encodeURIComponent(paymentNumber)}/reject`, { reason }),
  },

  investigations: {
    search: (params: Record<string, unknown>) =>
      getPaged<InvestigationListItem>('/api/v1/admin/investigations', params),
  },

  paymentLinks: {
    search: (params: Record<string, unknown>) => getPaged<PaymentLink>('/api/v1/payment-links', params),
    create: (body: Record<string, unknown>) => post<PaymentLink>('/api/v1/payment-links', body),
    get: (linkId: string) => get<PaymentLink>(`/api/v1/payment-links/${encodeURIComponent(linkId)}`),
    expire: (linkId: string) => post<PaymentLink>(`/api/v1/payment-links/${encodeURIComponent(linkId)}/expire`),
  },

  customers: {
    search: (params: Record<string, unknown>) => getPaged<Customer>('/api/v1/admin/customers', params),
    get: (customerNumber: string) => get<Customer>(`/api/v1/admin/customers/${encodeURIComponent(customerNumber)}`),
    anonymise: (customerNumber: string, reason: string) =>
      post<unknown>(`/api/v1/admin/customers/${encodeURIComponent(customerNumber)}/anonymise`, { reason }),
  },

  subscriptions: {
    search: (params: Record<string, unknown>) => getPaged<Subscription>('/api/v1/subscriptions', params),
    get: (subscriptionId: string) => get<Subscription>(`/api/v1/subscriptions/${encodeURIComponent(subscriptionId)}`),
    cancel: (subscriptionId: string, atPeriodEnd: boolean, reason?: string) =>
      post<Subscription>(`/api/v1/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`, {
        atPeriodEnd,
        reason,
      }),
    resume: (subscriptionId: string) =>
      post<Subscription>(`/api/v1/subscriptions/${encodeURIComponent(subscriptionId)}/resume`),
    changePlan: (subscriptionId: string, planCode: string, mode?: string) =>
      post<unknown>(`/api/v1/subscriptions/${encodeURIComponent(subscriptionId)}/change-plan`, { planCode, mode }),
    retryPayment: (subscriptionId: string) =>
      post<{
        subscriptionId: string;
        strategy: string;
        outcome: string;
        paymentId?: string;
        paymentUrl?: string;
        failureCode?: string;
        failureMessage?: string;
        nextRetryAt?: string;
      }>(`/api/v1/subscriptions/${encodeURIComponent(subscriptionId)}/retry-payment`),
  },

  plans: {
    search: (params: Record<string, unknown>) => getPaged<Plan>('/api/v1/plans', params),
    get: (planCode: string) => get<Plan>(`/api/v1/plans/${encodeURIComponent(planCode)}`),
    create: (body: Record<string, unknown>) => post<Plan>('/api/v1/plans', body),
    update: (planCode: string, body: Record<string, unknown>) =>
      put<Plan>(`/api/v1/plans/${encodeURIComponent(planCode)}`, body),
  },

  billing: {
    search: (params: Record<string, unknown>) => getPaged<BillingInvoice>('/api/v1/admin/billing/invoices', params),
    // Only the single-invoice endpoint returns line items, so the list payload stays light.
    get: (invoiceNumber: string) =>
      get<BillingInvoice>(`/api/v1/admin/billing/invoices/${encodeURIComponent(invoiceNumber)}`),
  },

  refunds: {
    search: (params: Record<string, unknown>) => getPaged<RefundListItem>('/api/v1/admin/refunds', params),
    approve: (refundNumber: string, notes?: string) =>
      post<unknown>(`/api/v1/admin/refunds/${encodeURIComponent(refundNumber)}/approve`, { notes }),
    reject: (refundNumber: string, reason: string) =>
      post<unknown>(`/api/v1/admin/refunds/${encodeURIComponent(refundNumber)}/reject`, { reason }),
    process: (refundNumber: string) =>
      post<unknown>(`/api/v1/admin/refunds/${encodeURIComponent(refundNumber)}/process`),
    approvals: (params: Record<string, unknown>) =>
      getPaged<ApprovalRequestView>('/api/v1/admin/refunds/approvals', params),
  },

  disputes: {
    search: (params: Record<string, unknown>) => getPaged<DisputeView>('/api/v1/admin/disputes', params),
    get: (disputeNumber: string) => get<DisputeView>(`/api/v1/admin/disputes/${encodeURIComponent(disputeNumber)}`),
    create: (body: Record<string, unknown>) => post<DisputeView>('/api/v1/admin/disputes', body),
    approve: (disputeNumber: string, notes?: string) =>
      post<DisputeView>(`/api/v1/admin/disputes/${encodeURIComponent(disputeNumber)}/approve`, { notes }),
    beginReview: (disputeNumber: string) =>
      post<DisputeView>(`/api/v1/admin/disputes/${encodeURIComponent(disputeNumber)}/review`),
    addEvidence: (disputeNumber: string, description: string, attachmentReference: string | undefined, submitToProvider: boolean) =>
      post<DisputeView>(`/api/v1/admin/disputes/${encodeURIComponent(disputeNumber)}/evidence`, {
        description,
        attachmentReference,
        submitToProvider,
      }),
    resolve: (disputeNumber: string, status: string, disputeFee?: number, notes?: string) =>
      post<DisputeView>(`/api/v1/admin/disputes/${encodeURIComponent(disputeNumber)}/resolve`, {
        status,
        disputeFee,
        notes,
      }),
  },

  settlements: {
    search: (params: Record<string, unknown>) => getPaged<SettlementView>('/api/v1/admin/settlements', params),
    get: (settlementNumber: string) =>
      get<SettlementView>(`/api/v1/admin/settlements/${encodeURIComponent(settlementNumber)}`),
    import: (providerCode: string, file: File) =>
      upload<SettlementView>('/api/v1/admin/settlements/import', file, { providerCode }),
    fetch: (providerCode: string, from: string, to: string) =>
      post<SettlementView[]>('/api/v1/admin/settlements/fetch', undefined, { providerCode, from, to }),
    close: (settlementNumber: string, notes?: string) =>
      post<unknown>(`/api/v1/admin/settlements/${encodeURIComponent(settlementNumber)}/close`, { notes }),
  },

  reconciliation: {
    search: (params: Record<string, unknown>) => getPaged<ReconciliationView>('/api/v1/admin/reconciliation', params),
    get: (reconciliationNumber: string) =>
      get<ReconciliationView>(`/api/v1/admin/reconciliation/${encodeURIComponent(reconciliationNumber)}`),
    items: (reconciliationNumber: string, params: Record<string, unknown>) =>
      getPaged<ReconciliationItemView>(
        `/api/v1/admin/reconciliation/${encodeURIComponent(reconciliationNumber)}/items`,
        params,
      ),
    run: (providerCode: string, fromDate: string, toDate: string, settlementNumber?: string) =>
      post<ReconciliationView>('/api/v1/admin/reconciliation/run', {
        providerCode,
        fromDate,
        toDate,
        settlementNumber,
      }),
    resolveItem: (itemId: string, notes: string | undefined, accepted: boolean) =>
      post<unknown>(`/api/v1/admin/reconciliation/items/${itemId}/resolve`, { notes, accepted }),
    escalateItem: (itemId: string, notes?: string) =>
      post<unknown>(`/api/v1/admin/reconciliation/items/${itemId}/escalate`, { notes }),
  },

  products: {
    all: () => get<Product[]>('/api/v1/admin/products'),
    get: (code: string) => get<Product>(`/api/v1/admin/products/${encodeURIComponent(code)}`),
    create: (body: Record<string, unknown>) => post<Product>('/api/v1/admin/products', body),
    update: (code: string, body: Record<string, unknown>) =>
      put<Product>(`/api/v1/admin/products/${encodeURIComponent(code)}`, body),
    setActive: (code: string, isActive: boolean) =>
      post<Product>(`/api/v1/admin/products/${encodeURIComponent(code)}/active`, undefined, { isActive }),
    rotateWebhookSecret: (code: string) =>
      post<Product>(`/api/v1/admin/products/${encodeURIComponent(code)}/webhook-secret`),
    addRedirectUrl: (code: string, url: string, kind: string, allowSubPaths: boolean) =>
      post<Product>(`/api/v1/admin/products/${encodeURIComponent(code)}/redirect-urls`, {
        url,
        kind,
        allowSubPaths,
      }),
    removeRedirectUrl: (code: string, id: string) =>
      del<Product>(`/api/v1/admin/products/${encodeURIComponent(code)}/redirect-urls/${id}`),
    tenants: (params: Record<string, unknown>) => getPaged<TenantView>('/api/v1/admin/products/tenants', params),
  },

  providers: {
    all: () => get<ProviderView[]>('/api/v1/admin/providers'),
    get: (code: string) => get<ProviderView>(`/api/v1/admin/providers/${encodeURIComponent(code)}`),
    setEnabled: (code: string, isEnabled: boolean) =>
      post<ProviderView>(`/api/v1/admin/providers/${encodeURIComponent(code)}/enabled`, undefined, { isEnabled }),
    upsertConfiguration: (code: string, body: Record<string, unknown>) =>
      put<ProviderView>(`/api/v1/admin/providers/${encodeURIComponent(code)}/configuration`, body),
    upsertCapability: (code: string, body: Record<string, unknown>) =>
      put<ProviderView>(`/api/v1/admin/providers/${encodeURIComponent(code)}/capabilities`, body),
    upsertMerchantAccount: (code: string, body: Record<string, unknown>) =>
      put<ProviderView>(`/api/v1/admin/providers/${encodeURIComponent(code)}/merchant-accounts`, body),
    closeCircuit: (code: string) =>
      post<ProviderView>(`/api/v1/admin/providers/${encodeURIComponent(code)}/close-circuit`),
  },

  webhooks: {
    inbound: (params: Record<string, unknown>) =>
      getPaged<WebhookEventView>('/api/v1/admin/webhooks/inbound', params),
    inboundDetail: (id: string) => get<WebhookEventView>(`/api/v1/admin/webhooks/inbound/${id}`),
    replayInbound: (id: string) => post<unknown>(`/api/v1/admin/webhooks/inbound/${id}/replay`),
    outbound: (params: Record<string, unknown>) => getPaged<DeliveryView>('/api/v1/admin/webhooks/outbound', params),
    outboundDetail: (id: string) => get<DeliveryView>(`/api/v1/admin/webhooks/outbound/${id}`),
    retryOutbound: (id: string) => post<unknown>(`/api/v1/admin/webhooks/outbound/${id}/retry`),
    resolveOutbound: (id: string) => post<unknown>(`/api/v1/admin/webhooks/outbound/${id}/resolve`),
  },

  events: {
    search: (params: Record<string, unknown>) => getPaged<OutboxEventView>('/api/v1/admin/events', params),
    get: (id: string) => get<OutboxEventView>(`/api/v1/admin/events/${id}`),
    retry: (id: string) => post<unknown>(`/api/v1/admin/events/${id}/retry`),
  },

  reports: {
    paymentsSummary: (params: Record<string, unknown>) =>
      get<PaymentPeriodSummary>('/api/v1/admin/reports/payments-summary', params),
    exportPaymentsSummary: (params: Record<string, unknown>, format: string) =>
      download('/api/v1/admin/reports/payments-summary/export', { ...params, format }, `payments-by-system.${format}`),
    paymentProof: (params: Record<string, unknown>) =>
      getPaged<PaymentProofRow>('/api/v1/admin/reports/payment-proof', params),
    paymentProofSummary: (params: Record<string, unknown>) =>
      get<PaymentProofSummary>('/api/v1/admin/reports/payment-proof/summary', params),
    exportPaymentProof: (params: Record<string, unknown>, format: string) =>
      download('/api/v1/admin/reports/payment-proof/export', { ...params, format }, `payment-proof.${format}`),
    providerReconciliation: (params: Record<string, unknown>) =>
      get<ProviderReconciliationReport>('/api/v1/admin/reports/provider-reconciliation', params),
    providerReconciliationItems: (params: Record<string, unknown>) =>
      getPaged<ProviderReconciliationItemRow>('/api/v1/admin/reports/provider-reconciliation/items', params),
    exportProviderReconciliation: (params: Record<string, unknown>, format: string) =>
      download(
        '/api/v1/admin/reports/provider-reconciliation/export',
        { ...params, format },
        `reconciliation.${format}`,
      ),
    available: () => get<ReportDefinition[]>('/api/v1/admin/reports'),
    run: (reportKey: string, params: Record<string, unknown>) =>
      get<ReportResult>(`/api/v1/admin/reports/${encodeURIComponent(reportKey)}`, params),
    export: (reportKey: string, params: Record<string, unknown>, format: string) =>
      download(
        `/api/v1/admin/reports/${encodeURIComponent(reportKey)}/export`,
        { ...params, format },
        `${reportKey}.${format === 'xlsx' ? 'xlsx' : format}`,
      ),
  },

  access: {
    users: (params: Record<string, unknown>) => getPaged<UserListItem>('/api/v1/admin/access/users', params),
    createUser: (body: Record<string, unknown>) => post<UserListItem>('/api/v1/admin/access/users', body),
    updateUser: (userId: string, body: Record<string, unknown>) =>
      put<UserListItem>(`/api/v1/admin/access/users/${userId}`, body),
    resetPassword: (userId: string, newPassword: string) =>
      post<unknown>(`/api/v1/admin/access/users/${userId}/reset-password`, { newPassword }),
    roles: () => get<RoleView[]>('/api/v1/admin/access/roles'),
    createRole: (body: Record<string, unknown>) => post<RoleView>('/api/v1/admin/access/roles', body),
    updateRole: (roleId: string, body: Record<string, unknown>) =>
      put<RoleView>(`/api/v1/admin/access/roles/${roleId}`, body),
    permissions: () => get<Record<string, string[]>>('/api/v1/admin/access/permissions'),
    apiClients: () => get<ApiClientView[]>('/api/v1/admin/access/api-clients'),
    createApiClient: (body: Record<string, unknown>) => post<ApiClientView>('/api/v1/admin/access/api-clients', body),
    rotateApiClientSecret: (clientId: string) =>
      post<ApiClientView>(`/api/v1/admin/access/api-clients/${encodeURIComponent(clientId)}/rotate-secret`),
    updateApiClientScopes: (clientId: string, scopes: string[]) =>
      put<ApiClientView>(`/api/v1/admin/access/api-clients/${encodeURIComponent(clientId)}/scopes`, { scopes }),
    setApiClientActive: (clientId: string, isActive: boolean) =>
      post<ApiClientView>(
        `/api/v1/admin/access/api-clients/${encodeURIComponent(clientId)}/active`,
        undefined,
        { isActive },
      ),
  },

  operations: {
    audit: (params: Record<string, unknown>) => getPaged<AuditLogView>('/api/v1/admin/operations/audit', params),
    settings: () => get<SettingView[]>('/api/v1/admin/operations/settings'),
    updateSetting: (key: string, value: string) =>
      put<SettingView>(`/api/v1/admin/operations/settings/${encodeURIComponent(key)}`, { value }),
    featureFlags: () => get<FeatureFlagView[]>('/api/v1/admin/operations/feature-flags'),
    toggleFeatureFlag: (key: string, isEnabled: boolean) =>
      post<FeatureFlagView>(`/api/v1/admin/operations/feature-flags/${encodeURIComponent(key)}`, undefined, {
        isEnabled,
      }),
    alerts: (params: Record<string, unknown>) => getPaged<AlertView>('/api/v1/admin/operations/alerts', params),
    acknowledgeAlert: (id: string) => post<unknown>(`/api/v1/admin/operations/alerts/${id}/acknowledge`),
    resolveAlert: (id: string, notes?: string) =>
      post<unknown>(`/api/v1/admin/operations/alerts/${id}/resolve`, { notes }),
    notifications: (params: Record<string, unknown>) =>
      getPaged<NotificationView>('/api/v1/admin/operations/notifications', params),
  },

  system: {
    health: () => get<SystemHealth>('/api/v1/admin/system/health'),
    eventCatalogue: () => get<string[]>('/api/v1/admin/system/event-catalogue'),
    errorCatalogue: () =>
      get<{ code: string; messageEn: string; messageAr: string }[]>('/api/v1/admin/system/error-catalogue'),
  },
};

export type { PagedResponse };
